"use client";

import { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

// 컨테이너 크기를 측정하는 훅 (ResponsiveContainer 대체)
function useContainerSize(ref: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

// ── 결론 분포 도넛 ──

const CONCLUSION_COLORS: Record<string, string> = {
  positive: "#22c55e",
  mixed: "#f59e0b",
  neutral: "#9ca3af",
  negative: "#ef4444",
};
const CONCLUSION_LABELS: Record<string, string> = {
  positive: "긍정",
  mixed: "혼합",
  neutral: "중립",
  negative: "부정",
};

interface ConclusionDonutProps {
  dist: Record<string, number>;
}

export function ConclusionDonut({ dist }: ConclusionDonutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerSize(ref);
  const total = Object.values(dist).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const data = Object.entries(dist)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: CONCLUSION_LABELS[key] || key,
      value,
      color: CONCLUSION_COLORS[key] || "#d1d5db",
    }));

  const posPct = Math.round(((dist.positive || 0) / total) * 100);

  return (
    <div className="flex items-center gap-4">
      <div ref={ref} className="relative h-[120px] w-[120px] shrink-0">
        {width > 0 && height > 0 && (
          <PieChart width={width} height={height}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={36}
              outerRadius={54}
              dataKey="value"
              strokeWidth={0}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Pie>
          </PieChart>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-green-600">{posPct}%</span>
          <span className="text-muted-foreground text-[10px]">긍정</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2 text-xs">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: d.color }}
            />
            <span className="text-muted-foreground">{d.name}</span>
            <span className="font-semibold">{d.value}편</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 연도별 추이 바차트 ──

interface YearTrendProps {
  dist: Record<string, number>;
}

export function YearTrendChart({ dist }: YearTrendProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerSize(ref);
  const entries = Object.entries(dist)
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => a.year.localeCompare(b.year));

  if (entries.length === 0) return null;

  return (
    <div>
      <h4 className="text-muted-foreground mb-2 text-sm font-semibold">연도별 연구 추이</h4>
      <div ref={ref} className="h-[140px]">
        {width > 0 && height > 0 && (
          <BarChart
            data={entries}
            width={width}
            height={height}
            margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
          >
            <XAxis
              dataKey="year"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
              formatter={(value) => [`${value}편`, "논문"]}
              labelFormatter={(label) => `${label}년`}
            />
            <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        )}
      </div>
    </div>
  );
}

// ── 연구설계 분포 도넛 ──

const DESIGN_COLORS = ["#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe", "#ede9fe", "#6d28d9", "#4c1d95"];
const DESIGN_LABELS: Record<string, string> = {
  rct: "무작위 대조",
  parallel_rct: "평행 RCT",
  split_face_rct: "반면 RCT",
  crossover_rct: "교차 RCT",
  cohort: "코호트",
  case_series: "증례 시리즈",
  case_report: "증례 보고",
  review: "리뷰/메타",
  meta_analysis: "메타분석",
  systematic_review: "체계적 리뷰",
  retrospective: "후향적",
  prospective: "전향적",
  before_after: "전후 비교",
  comparative: "비교 연구",
  pilot: "파일럿",
  split_face: "반면 비교",
  case_control: "환자-대조",
  observational: "관찰 연구",
  other: "기타",
};

interface DesignDistProps {
  dist: Record<string, number>;
}

export function DesignDistChart({ dist }: DesignDistProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerSize(ref);
  const total = Object.values(dist).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const data = Object.entries(dist)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([key, value], i) => ({
      name: DESIGN_LABELS[key] || key,
      value,
      color: DESIGN_COLORS[i % DESIGN_COLORS.length],
    }));

  return (
    <div>
      <h4 className="text-muted-foreground mb-2 text-sm font-semibold">연구 설계 분포</h4>
      <div className="flex items-center gap-4">
        <div ref={ref} className="h-[120px] w-[120px] shrink-0">
          {width > 0 && height > 0 && (
            <PieChart width={width} height={height}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={32}
                outerRadius={54}
                dataKey="value"
                strokeWidth={0}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(value) => [`${value}편`, ""]}
              />
            </PieChart>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {data.slice(0, 5).map((d) => (
            <div key={d.name} className="flex items-center gap-2 text-xs">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: d.color }}
              />
              <span className="text-muted-foreground">{d.name}</span>
              <span className="font-semibold">{Math.round((d.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
